/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, Inject, Input, NgZone, OnChanges, OnInit, SimpleChanges, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import {resourceOptionParams} from './signal-admin-resource-api';

import template from './reach-analysis.component.html';
import {Uuid} from '../../../components/util/uuid';
import {ADN_TARGETING_TYPES} from '../../targeting/targeting-constants';
import {calculateAllocation, calculateHighchartsAllocation} from './allocation-calculator';
import {DataViewApiService} from './data-view-api.service';
import {ReachEstimateApiService} from '../../reach-analysis/reach-estimate-api.service';
import {TargetingEditorModalService} from '../../targeting/targeting-editor-modal.service';
import {OgTranslateService} from '../filters/og-translate.service';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {OrderApiService} from '../../order/order-api.service';
import {TeamApiService} from '../../admin/team/team-api.service';
import {CreativeApiService} from '../../creative/creative-api.service';
import {LineItemApiService} from '../../line-item/line-item-api.service';
import {MarketplaceProductApiService} from '../../admin/marketplace-product/marketplace-product-api.service';
import {ProductApiService} from '../../admin/product/product-api.service';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {SearchApiService} from './search-api.service';
import {deferSignalStateWrite} from '../util/template-state';
import {StateService} from '@uirouter/core';


const FORECAST_TYPES = ['LINE_ITEM', 'CUSTOM'];

import {TARGETING_IMPORTS} from '../../targeting/targeting-imports';
import {TargetSummaryComponent} from '../../targeting/target-summary.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetSummaryComponent],
  selector: 'adn-reach-analysis',
  template
})
export class ReachAnalysisComponent implements OnInit, OnChanges {
  @Input() model: any;
  @Input() copyHook: any;
  @Input() targeting: any;
  @Input() callbackHook: any;
  @Input() targetingType: string;
  @Input() type: string;
  @Input() objectType: string;
  @Input() creatives: any;

  get resolvedObjectType(): string {
    return this.objectType;
  }

  get resolvedTargetingType(): string {
    return this.targetingType;
  }

  readonly math = Math;
  readonly forecastTypes = FORECAST_TYPES;

  sectionPerms: any;
  canSeeCharts: boolean;
  canSeeCalendars: boolean;
  noDataSponsorshipMessage = 'No competitors with sponsorship were found';
  noDataAuctionMessage = 'No auction competitors were found';

  hasResults = false;
  hasBegun = false;
  isLineItem = false;
  isGeneral = false;
  isTargeted = false;
  translation = 'A line item could deliver:';
  objectTrans: string;
  private readonly lineItemState = signal<any>(null);
  excludeLineItem = true;
  forecastType: string;
  forecastMethod = 'NORMAL';
  isAxAdvertiser: boolean;
  isSelfService: boolean;

  reachFields = [
    {id: 'name', name: 'Name'},
    {id: 'startDate', name: 'Start Date'},
    {id: 'endDate', name: 'End Date'},
    {id: 'bidSpecification', name: 'Bid'},
    {id: 'impressionObjective', name: 'Objective'},
    {id: 'impressionDelivery', name: 'Delivered'},
    {id: 'overlapPercent', name: 'Allocation Percentage'},
    {id: 'dateOverlap', name: 'Date Overlap'},
    {id: 'matchingSimilarity', name: 'Matching Similarity'},
    {id: 'impressionAllocationEstimate', name: 'Estimated Allocation'},
    {id: 'tierPriority', name: 'Tier and Prio'}
  ];

  sorting = {
    orderByParam: 'matchingSimilarity',
    asc: true
  };

  private readonly teamsState = signal<any[]>([]);
  private readonly productsState = signal<any[]>([]);
  private readonly axProductsState = signal<any[]>([]);
  tiers: any[] = [];
  private readonly lineItemResultsState = signal<any[]>([]);
  hideTargeting: boolean;
  private readonly forecastModelState = signal<any>({});
  private readonly forecastStatesState = signal<Record<string, any>>({
    LINE_ITEM: {},
    CUSTOM: {}
  });
  forecastedResults: string;
  creativeDims: any[] = [];

  private readonly isCopyPendingState = signal(false);
  private readonly copyPendingState = signal(false);
  initialised = false;

  get lineItem(): any { return this.lineItemState(); }
  set lineItem(value: any) { this.lineItemState.set(value); }
  get teams(): any[] { return this.teamsState(); }
  set teams(value: any[]) { this.teamsState.set(value || []); }
  get products(): any[] { return this.productsState(); }
  set products(value: any[]) { this.productsState.set(value || []); }
  get axProducts(): any[] { return this.axProductsState(); }
  set axProducts(value: any[]) { this.axProductsState.set(value || []); }
  get lineItemResults(): any[] { return this.lineItemResultsState(); }
  set lineItemResults(value: any[]) { this.lineItemResultsState.set(value || []); }
  get forecastModel(): any { return this.forecastModelState(); }
  set forecastModel(value: any) { this.forecastModelState.set(value || {}); }
  get forecastStates(): Record<string, any> { return this.forecastStatesState(); }
  set forecastStates(value: Record<string, any>) { this.forecastStatesState.set(value); }
  get isCopyPending(): boolean { return this.isCopyPendingState(); }
  set isCopyPending(value: boolean) { this.isCopyPendingState.set(value); }
  get copyPending(): boolean { return this.copyPendingState(); }
  set copyPending(value: boolean) { this.copyPendingState.set(value); }

  private defaultTeam: any;
  private readonly sponsorshipAllocationStore: Record<string, any> = {};
  private readonly auctionAllocationStore: Record<string, any> = {};
  private readonly tierEntrySources: Record<string, any> = {};
  private readonly tierEntryCache: Record<string, Array<{key: string; value: any}>> = {};
  private prevForecast: any;
  private prevLineItemId: any;

  constructor(
    private readonly reachEstimateApi: ReachEstimateApiService,
    private readonly dataViewApi: DataViewApiService,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly ngZone: NgZone,
    private readonly translate: TranslateService,
    private readonly adnLog: AdnLogService,
    private readonly $state: StateService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly searchApi: SearchApiService,
    private readonly orderApi: OrderApiService,
    @Inject(TeamApiService) private readonly Team: TeamApiService,
    @Inject(ProductApiService) private readonly Product: ProductApiService,
    @Inject(MarketplaceProductApiService) private readonly MarketplaceProduct: MarketplaceProductApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly ogTranslate: OgTranslateService,
  ) {
  }

  ngOnInit(): void {
    this.sectionPerms = this.LocalUserPermissions.getSectionPerms();
    this.canSeeCharts = this.localUserProfile.canSeeReachCharts();
    this.canSeeCalendars = this.LocalNetworkProfile.canSeeReachCalendars() || this.localUserProfile.canSeeReachCharts();
    this.isAxAdvertiser = this.LocalUserPermissions.isAxAdvertiser();
    this.isSelfService = this.LocalUserPermissions.isSelfService();

    this.hasResults = false;
    this.hasBegun = false;

    this.refreshContextFlags();

    this.translation = 'A line item could deliver:';
    if (this.isTargeted && !this.isAxAdvertiser && !this.isSelfService) {
      this.objectTrans = this.resolvedObjectType === 'SITE' ? 'site' : this.resolvedObjectType === 'AD_UNIT' ? 'ad unit' : 'object';
      this.translation = 'This ' + this.objectTrans + ' has the following available:';
    }
    if (this.resolvedObjectType === 'TARGETING') {
      this.objectTrans = this.resolvedTargetingType === 'CREATIVE' ? 'creative' : 'line item';
      this.translation = 'The targeting applied to this ' + this.objectTrans + ' could deliver:';
    }

    this.lineItem = this.model || {};
    this.excludeLineItem = true;
    this.forecastType = this.isLineItem && this.resolvedObjectType !== 'TARGETING' ? 'LINE_ITEM' : 'CUSTOM';

    if (this.resolvedObjectType === 'TARGETING' && _.get(this.lineItem, ['order']) && !_.get(this.lineItem, ['order', 'team', 'id'])) {
      this.orderApi.get(this.lineItem.order.id).subscribe((order: any) => {
        this.ngZone.run(() => {
          this.lineItem = {...this.lineItem, order};
        });
      });
    }

    this.updateOrder();
    this.loadTeams();

    if (this.isSelfService) {
      firstValueFrom(this.Product.listOptions(resourceOptionParams())).then((page: any) => {
        this.ngZone.run(() => {
          this.products = page.results;
        });
      });
    }

    if (this.isAxAdvertiser) {
      firstValueFrom(this.MarketplaceProduct.listOptions(resourceOptionParams())).then((page: any) => {
        this.ngZone.run(() => {
          this.axProducts = page.results;
        });
      });
    }

    this.tiers = _.cloneDeep(this.LocalNetworkProfile.get('tiers'));

    this.hideTargeting = this.isAxAdvertiser || this.resolvedObjectType === 'TARGETING';

    this.initForecastModel();

    if (this.copyHook) {
      this.copyHook.copyFuncs = this.copyHook.copyFuncs || [];
      this.copyHook.copyFuncs.push((copyType: string, forecastModel: any) => {
        if (copyType === this.type) {
          return;
        }
        this.forecastModel = _.cloneDeep(forecastModel);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model || changes.objectType) {
      this.refreshContextFlags();
      const modelId = this.model?.id;
      const hadModelId = changes.model?.previousValue?.id;
      if (changes.model) {
        const previousOrderId = _.get(this.lineItem, 'order.id');
        this.lineItem = this.model || {};
        if (!changes.model.firstChange && previousOrderId !== _.get(this.lineItem, 'order.id')) {
          this.updateOrder();
        }
        this.assignDefaultTeam();
      }
      if (modelId && modelId !== hadModelId && this.isTargeted) {
        this.initForecastModel();
      }
    }
  }

  makeSortQuery(param: {id: string}): void {
    if (this.sorting.orderByParam === param.id) {
      this.sorting.asc = !this.sorting.asc;
    } else {
      this.sorting.orderByParam = param.id;
      this.sorting.asc = true;
    }
  }

  sortedCompetitors(competitors: any[]): any[] {
    if (!competitors?.length) {
      return [];
    }
    const sorted = competitors.slice().sort((a, b) => {
      const cmp = this.sortComparator(
        {value: this.getSortValue(a, this.sorting.orderByParam)},
        {value: this.getSortValue(b, this.sorting.orderByParam)}
      );
      return this.sorting.asc ? cmp : -cmp;
    });
    return sorted;
  }

  tierEntries(fType: string): Array<{key: string; value: any}> {
    const byTier = this.forecastStates[fType]?.forecast?.byTier || {};
    if (this.tierEntrySources[fType] !== byTier) {
      this.tierEntrySources[fType] = byTier;
      this.tierEntryCache[fType] = Object.keys(byTier).map((key) => ({key, value: byTier[key]}));
    }
    return this.tierEntryCache[fType];
  }

  trackByTierKey(_index: number, entry: {key: string}): string {
    return entry.key;
  }

  fieldTranslate(key: string): string {
    return this.ogTranslate.transform(key);
  }

  fieldTranslateLower(key: string): string {
    return (this.fieldTranslate(key) || '').toLowerCase();
  }

  searchLineItems = (searchElement: {search: string}): void => {
    this.searchApi.search({q: searchElement.search, types: 'LineItem'}).subscribe((data) => {
      this.ngZone.run(() => {
        this.lineItemResults = data.searchResults;
      });
    });
  };

  onProductChange(): void {
    const selProduct = this.forecastModel.axProduct;
    const optionalTargeting = _.get(selProduct, ['optionalTargetTypes'], []);
    this.hideTargeting = this.isAxAdvertiser && (!this.forecastModel.axProduct || optionalTargeting.length === 0);
  }

  editTarget(): void {
    let availableTargets;
    if (this.isAxAdvertiser) {
      const optionalTargeting = _.get(this.forecastModel, ['axProduct', 'optionalTargetTypes'], []);
      const optionalTargetIds = _.map(_.filter(ADN_TARGETING_TYPES, (tt: any) => {
        return _.indexOf(optionalTargeting, tt.apiType) > -1;
      }), 'id');
      availableTargets = _.pick(ADN_TARGETING_TYPES, optionalTargetIds);
    }

    this.targetingEditorModalService.open({
      lineItemType: 'NORMAL',
      modelLineItem: null,
      pageType: 'reach',
      reachEnabled: undefined,
      overridableTargeting: undefined,
      targetingMode: null,
      mandatoryTargets: undefined,
      availableTargets,
      targeting: this.forecastModel.targeting,
      isLockedDown: false
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      this.ngZone.run(() => {
        const targeting = _.cloneDeep(_.assign({}, this.forecastModel.targeting, wc.wc));
        this.forecastModel = {...this.forecastModel, targeting};
      });
    });
  }

  addDimensions(): void {
    this.forecastModel.dimensions = this.forecastModel.dimensions || [];
    this.forecastModel.dimensions.push([]);
  }

  removeDimensions(index: number): void {
    _.pullAt(this.forecastModel.dimensions, index);
  }

  copyLineItem(): void {
    this.isCopyPending = true;
    const newLineItem = {
      type: 'AUCTION',
      companionAds: 'NONE',
      deliveryCurve: 'EVEN',
      bidSpecification: {cpm: {}, cpc: {}},
      id: Uuid.generate(),
      name: 'Line Item from Reach Analysis',
      startDate: this.forecastModel.startDate,
      endDate: this.forecastModel.endDate,
      targeting: this.forecastModel.targeting
    };

    firstValueFrom(this.LineItem.save(newLineItem)).then((savedLineItem: any) => {
      const uniqueDims = _.uniqWith(this.forecastModel.dimensions, _.isEqual);
      _.forEach(uniqueDims, (dims: any[], index: number) => {
        const newCreative = {
          assets: [],
          id: Uuid.generate(),
          name: 'Creative ' + (index + 1) + ' for ' + savedLineItem.name,
          lineItem: {id: savedLineItem.id},
          width: dims[0],
          height: dims[1]
        };
        firstValueFrom(this.Creative.save(newCreative)).then(() => {
          if (index + 1 >= uniqueDims.length) {
            this.ngZone.run(() => {
              this.isCopyPending = false;
              this.$state.go('app.adv.line-items.line-item', {id: savedLineItem.id});
            });
          }
        });
      });
    });
  }

  copyFromSearchedLineItem(): void {
    const lineItemId = _.isObject(this.lineItem?.id) ? this.lineItem.id.id : this.lineItem?.id;
    if (!lineItemId) {
      return;
    }
    this.copyPending = true;
    this.initialised = true;
    this.dataViewApi.getView('LineItemView', {id: lineItemId}, false).subscribe((results) => {
      this.ngZone.run(() => {
        this.lineItem = results.lineItem;
        this.lineItem.creatives = results.creatives;
        this.lineItem.team = _.get(results.order, 'team');

        if (this.lineItem.startDate) {
          this.lineItem.modStartDate = this.lineItem.startDate;
        }
        if (this.lineItem.endDate) {
          this.lineItem.modEndDate = this.lineItem.endDate;
        }

        this.copyPending = false;
        this.copyFromLineItem();
      });
    });
  }

  copyDetails(): void {
    this.copyHook.initCompare();
    _.forEach(_.get(this.copyHook, 'copyFuncs'), (f: (type: string, forecastModel: any) => void) => {
      f(this.type, this.forecastModel);
    });
  }

  copyFromLineItem(): void {
    const creatives = _.values(this.lineItem.creatives || this.creatives);
    const adType = this.forecastModel.adType || 'STANDARD';
    const tierId = _.get(this.lineItem, 'tier.id') || _.get(this.forecastModel, 'tier.id') ||
      this.LocalNetworkProfile.get('defaultTierId');
    const tier = _.find(this.tiers, ['id', tierId]);
    this.forecastModel = {};
    this.forecastModel.objectives = _.cloneDeep(this.lineItem.objectives);
    this.forecastModel.startDate = _.cloneDeep(this.lineItem.startDate);
    this.forecastModel.modStartDate = _.cloneDeep(this.lineItem.modStartDate || this.lineItem.startDate);
    this.forecastModel.endDate = _.cloneDeep(this.lineItem.endDate);
    this.forecastModel.modEndDate = _.cloneDeep(this.lineItem.modEndDate || this.lineItem.endDate);
    this.forecastModel.targeting = _.cloneDeep(this.lineItem.targeting);
    this.forecastModel.team = this.defaultTeam || this.lineItem.team;
    this.forecastModel.cpm = _.cloneDeep(this.lineItem.bidSpecification.cpm);
    this.forecastModel.tier = tier;
    this.forecastModel.adType = adType;

    this.forecastModel.dimensions = [];
    _.forEach(creatives, (creative: any) => {
      if (creative.width && creative.height) {
        this.forecastModel.dimensions.push([creative.width, creative.height]);
      }
    });
    this.forecastModel.dimensions[0] = _.isArray(this.forecastModel.dimensions[0]) ? this.forecastModel.dimensions[0] : [];

    this.forecastModel.dimensions = _.uniqWith(this.forecastModel.dimensions, _.isEqual);

    const creative: any = _.head(creatives);
    if (creative) {
      this.forecastModel.width = creative.width;
      this.forecastModel.height = creative.height;
    }
  }

  triggerForecast(chartRerun?: boolean): void {
    if (_.isFunction(_.get(this.callbackHook, ['onReachButtonClicked']))) {
      this.callbackHook.onReachButtonClicked();
    }

    const pendingParam = chartRerun ? 'accuracyIsPending' : 'isPending';

    this.forecastModel.isStandard = this.forecastModel.adType === 'STANDARD';
    this.forecastModel.isVideo = this.forecastModel.adType === 'VAST';
    this.forecastModel.isNative = this.forecastModel.adType === 'NATIVE';

    const fType = this.forecastType;
    this.forecastStates[fType] = this.forecastStates[fType] || {};
    this.forecastStates[fType][pendingParam] = true;
    this.forecastStates[fType].hasBegun = true;
    this.forecastStates[fType].hasResults = false;
    this.forecastStates[fType].error = false;
    this.publishForecastState(fType);
    this.forecastedResults = this.forecastMethod;

    const lineItemId = _.get(this.lineItem, 'id');
    let forecast: any = {
      accuracy: this.forecastModel.accuracy
    };

    if (this.forecastType === 'CUSTOM') {
      forecast = _.cloneDeep(this.forecastModel);
      forecast.team = _.get(forecast.team, 'id');
      forecast.product = _.get(forecast.product, 'id');
      forecast.axProduct = _.get(forecast.axProduct, 'id');
      forecast.startDate = _.isDate(this.forecastModel.modStartDate) ? this.forecastModel.modStartDate : null;
      forecast.endDate = _.isDate(this.forecastModel.modEndDate) ? this.forecastModel.modEndDate : null;
      if (this.excludeLineItem) {
        forecast.excludeId = _.get(this.lineItem, 'id');
      }
      if (this.isAxAdvertiser || this.isSelfService) {
        forecast.creativeTargeting = forecast.targeting;
        forecast.targeting = null;
      }

      forecast.dimensions = forecast.dimensions || [];
      forecast.dimensions = _.filter(forecast.dimensions, (dims: any) => {
        return _.isArray(dims) && dims[0] && dims[1];
      });

      forecast.cpm = forecast.cpm || null;
      forecast.tier = forecast.tier || {};
      forecast.tier = _.get(forecast.tier, 'id');

      _.forEach(forecast.objectives, (value: any, key: string) => {
        if (!_.isFinite(value)) {
          delete forecast.objectives[key];
        }
      });
      if (_.isEmpty(forecast.objectives)) {
        delete forecast.objectives;
      }
      if (this.targeting) {
        forecast.targeting = this.targeting;
      }
    }

    delete forecast.width;
    delete forecast.height;
    forecast.accuracy = forecast.accuracy || 0.6;

    let saveLineItemId = lineItemId;
    if (this.forecastType === 'CUSTOM') {
      saveLineItemId = null;
    }

    if (this.resolvedObjectType === 'TARGETING') {
      forecast.excludeCompetitors = true;
      forecast.team = _.get(this.lineItem, 'order.team.id', null);
      forecast.axProduct = _.get(this.lineItem, ['axProduct', 'id']);
      forecast.cpm = _.get(this.lineItem, ['bidSpecification', 'cpm']);
      if (!forecast.cpm) {
        delete forecast.cpm;
      }

      const validCreatives = _.filter(_.get(this.lineItem, 'creativesForReach', []), (creative: any) => {
        return creative.dimensionType !== 'FIXED' || (_.isFinite(creative.width) && _.isFinite(creative.height));
      });
      const hasFlexible = _.find(validCreatives, (creative: any) => {
        return creative.dimensionType !== 'FIXED';
      });
      forecast.dimensions = hasFlexible ? [] : _.uniqWith(_.map(validCreatives, (creative: any) => {
        return [creative.width, creative.height];
      }), _.isEqual);

      if (this.resolvedTargetingType === 'CREATIVE') {
        forecast.targeting = _.get(this.lineItem, ['targeting']);
        forecast.creativeTargeting = this.targeting;
      }
    }

    this.creativeDims = _.cloneDeep(forecast.dimensions);

    this.reachEstimateApi.save(
      saveLineItemId,
      forecast,
      this.resolvedObjectType === 'SITE' || this.resolvedObjectType === 'AD_UNIT' ? {availability: true} : {}
    ).subscribe({
      next: (results) => {
        deferSignalStateWrite(() => {
          this.handleForecastSuccess(fType, pendingParam, forecast, saveLineItemId, results);
          this.publishForecastState(fType);
        });
      },
      error: (err) => {
        deferSignalStateWrite(() => {
          this.forecastStates[fType][pendingParam] = false;
          this.forecastStates[fType].error = true;
          if (!err || !err.data) {
            this.forecastStates[fType].errorMessage = 'An unspecified error took place.';
            this.adnLog.warn('Unspecified reach estimate error: ', err);
          } else {
            this.forecastStates[fType].errorMessage = err.data.error || this.translate.instant(err.data.text, err.data.parameters);
          }
          this.publishForecastState(fType);
        });
      }
    });
  }

  bidSpecificationEntries(bidSpecification: any): Array<{key: string; value: any}> {
    if (!bidSpecification) {
      return [];
    }
    return Object.keys(bidSpecification)
      .filter((key) => {
        const val = bidSpecification[key];
        // Only money-style bid fields (cpm/cpc/cpa); skip objects like optimiseFor.
        return _.isFinite(_.get(val, 'amount')) && val.amount >= 0;
      })
      .map((key) => ({key, value: bidSpecification[key]}));
  }

  matchedTrafficPercent(fType: string): number {
    const forecast = this.forecastStates[fType]?.forecast;
    if (!forecast || forecast.numSamples <= 0) {
      return 0;
    }
    return (100 * forecast.matchedSamples / forecast.numSamples);
  }

  private refreshContextFlags(): void {
    this.isLineItem = !!(this.model && (this.resolvedObjectType === 'LINE_ITEM' || this.resolvedObjectType === '' || this.resolvedObjectType === 'TARGETING'));
    this.isGeneral = !this.model && !this.resolvedObjectType && !!this.type && this.resolvedObjectType !== 'TARGETING';
    this.isTargeted = !!(this.model && (this.resolvedObjectType !== 'LINE_ITEM' && this.resolvedObjectType !== '') && this.resolvedObjectType !== 'TARGETING');
  }

  private initForecastModel(): void {
    this.forecastModel = {};
    this.forecastModel.targeting = {};
    this.forecastModel.adType = 'STANDARD';
    this.forecastModel.isStandard = true;
    this.forecastModel.isVideo = false;
    this.forecastModel.isNative = false;

    if (this.resolvedObjectType === 'AD_UNIT') {
      this.forecastModel.targeting = {
        adUnitTarget: {
          adUnits: [{id: this.model.id}]
        }
      };
    } else if (this.resolvedObjectType === 'SITE') {
      this.forecastModel.targeting = {
        siteTarget: {
          sites: [{id: this.model.id}]
        }
      };
    }

    this.forecastModel.dimensions = _.isArray(this.forecastModel.dimensions) ? this.forecastModel.dimensions : [[]];
    this.forecastModel.dimensions[0] = _.isArray(this.forecastModel.dimensions[0]) ? this.forecastModel.dimensions[0] : [];

    const networkDefaults = this.LocalNetworkProfile.getDefaults();
    const networkToday = this.adnTimeShifter.getMomentInNetworkTimezone().startOf('day');
    const startDate = networkToday.toDate();
    const endDate = networkToday.clone().add(networkDefaults.lineItemDaysApart, 'day').endOf('day').toDate();
    this.forecastModel.startDate = startDate;
    this.forecastModel.endDate = endDate;
    if (this.forecastModel.startDate) {
      this.forecastModel.modStartDate = this.forecastModel.startDate;
    }
    if (this.forecastModel.endDate) {
      this.forecastModel.modEndDate = this.forecastModel.endDate;
    }
  }

  private loadTeams(): void {
    firstValueFrom(this.Team.listOptions()).then((page: any) => {
      this.ngZone.run(() => {
        const allGroups = page.results;
        this.teams = _.filter(allGroups, (g: any) => {
          return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
        });
        this.assignDefaultTeam();
      });
    });
  }

  private assignDefaultTeam(): void {
    this.defaultTeam = _.find(this.teams || [], ['id', _.get(this.lineItem, 'order.team.id')]);
  }

  private updateOrder(): void {
    const orderId = _.get(this.lineItem, 'order.id');
    if (orderId) {
      this.orderApi.get(orderId).subscribe((ord: any) => {
        this.ngZone.run(() => {
          this.lineItem = {...this.lineItem, order: ord};
          this.assignDefaultTeam();
        });
      });
    }
  }

  private handleForecastSuccess(fType: string, pendingParam: string, forecast: any, lineItemId: any, results: any): void {
    // Assign forecast before clearing pending so templates never see isPending=false
    // with a missing forecast (that produced "reading 'targetingPercent'" in prod).
    const forecastResult = results || {};
    this.forecastStates[fType].forecast = forecastResult;
    this.forecastStates[fType].forecast.byTier = {};
    this.forecastStates[fType].hasResults = true;

    const paramsChanged = !_.isEqual(this.prevForecast, forecast) || !_.isEqual(this.prevLineItemId, lineItemId);
    this.prevForecast = _.cloneDeep(forecast);
    this.prevLineItemId = _.cloneDeep(lineItemId);

    this.sponsorshipAllocationStore[fType] = this.sponsorshipAllocationStore[fType] || {};
    this.auctionAllocationStore[fType] = this.auctionAllocationStore[fType] || {};
    if (paramsChanged) {
      this.sponsorshipAllocationStore[fType] = {};
      this.auctionAllocationStore[fType] = {};
    }

    if (forecastResult.allocationDates) {
      const data = calculateAllocation(this.auctionAllocationStore[fType], forecastResult.allocationDates, forecastResult.auctionAllocation);
      const auctionCalendar = calculateHighchartsAllocation(data);

      const sponsorshipData = calculateAllocation(this.sponsorshipAllocationStore[fType], forecastResult.allocationDates, forecastResult.sponsorshipAllocation);
      const sponsorshipCalendar = calculateHighchartsAllocation(sponsorshipData);

      this.forecastStates[fType].auctionDates = _.keys(this.auctionAllocationStore[fType]);
      this.forecastStates[fType].sponsorshipDates = _.keys(this.sponsorshipAllocationStore[fType]);
      this.forecastStates[fType].auctionCalendar = auctionCalendar;
      this.forecastStates[fType].sponsorshipCalendar = sponsorshipCalendar;
      this.forecastStates[fType].auctionRedraw = this.forecastStates[fType].auctionRedraw || {};
      this.forecastStates[fType].sponsorshipRedraw = this.forecastStates[fType].sponsorshipRedraw || {};

      if (_.isFunction(this.forecastStates[fType].auctionRedraw.execute)) {
        setTimeout(() => {
          this.forecastStates[fType].auctionRedraw.execute();
          this.forecastStates[fType].sponsorshipRedraw.execute();
        }, 100);
      }
    }

    this.forecastStates[fType].creativeScaling = forecastResult.matchedCreativePercentage >= 0 ? forecastResult.matchedCreativePercentage / 100.0 : 1;
    if (this.resolvedTargetingType === 'CREATIVE' && this.forecastStates[fType].creativeScaling === 1) {
      this.forecastStates[fType].forecast.targetingPercent = 100;
      this.forecastStates[fType].forecast.targetingScore = 10;
    }

    this.forecastStates[fType].forecast.compArray = _.map(forecastResult.competitors, (c: any, key: string) => {
      this.forecastStates[fType].forecast.byTier[c.tierPriority] = this.forecastStates[fType].forecast.byTier[c.tierPriority] || {
        tier: c.tier,
        imps: 0,
        rends: 0,
        tierName: c.tierName,
        tierPriority: c.tierPriority,
        liCount: 0,
        liImpCount: 0,
        liMinMaxCount: 0,
        liOverlapCount: 0,
        maxLiReach: 0
      };
      const tierInfo = this.forecastStates[fType].forecast.byTier[c.tierPriority];
      tierInfo.imps += c.impressionAllocationEstimate || 0;
      tierInfo.rends += c.renderedAllocationEstimate || 0;

      if (c.impressionAllocationEstimate > 0) {
        tierInfo.liImpCount++;
      }

      tierInfo.tierMin = c.tierMinimum;
      tierInfo.tierMax = c.tierMaximum;

      tierInfo.maxLiReach = !tierInfo.maxLiReach || c.overlapReach > tierInfo.maxLiReach ? c.overlapReach : tierInfo.maxLiReach;
      tierInfo.liOverlapCount += c.overlapReach;
      if (tierInfo.liOverlapCount > this.forecastStates[fType].forecast.reach) {
        tierInfo.liOverlapCount = this.forecastStates[fType].forecast.reach;
      }

      if (c.overlapPercent > 0) {
        tierInfo.noMinMax = tierInfo.noMinMax || (!c.impressionObjective && !c.clickObjective && !c.renderedObjective && !c.visibleObjective && !c.viewableObjective && !c.conversionObjective);

        if (tierInfo.noMinMax) {
          tierInfo.liMinMaxCount++;
        }

        tierInfo.liCount++;

        const theCpm = _.get(c.bidSpecification, 'cpm') || {amount: 0};
        tierInfo.minCpm = !tierInfo.minCpm || theCpm.amount < tierInfo.minCpm.amount ? theCpm : tierInfo.minCpm;
        tierInfo.maxCpm = !tierInfo.maxCpm || theCpm.amount > tierInfo.maxCpm.amount ? theCpm : tierInfo.maxCpm;

        const theCpc = _.get(c.bidSpecification, 'cpc') || {amount: 0};
        tierInfo.minCpc = !tierInfo.minCpc || theCpc.amount < tierInfo.minCpc.amount ? theCpc : tierInfo.minCpc;
        tierInfo.maxCpc = !tierInfo.maxCpc || theCpc.amount > tierInfo.maxCpc.amount ? theCpc : tierInfo.maxCpc;

        const theCpa = _.get(c.bidSpecification, 'cpa') || {amount: 0};
        tierInfo.minCpa = !tierInfo.minCpa || theCpa.amount < tierInfo.minCpa.amount ? theCpc : tierInfo.minCpa;
        tierInfo.maxCpa = !tierInfo.maxCpa || theCpa.amount > tierInfo.maxCpa.amount ? theCpc : tierInfo.maxCpa;
      }

      return _.merge({}, c, {lineItemId: key});
    });

    this.forecastStates[fType].forecast.totalAllocated = _.reduce(this.forecastStates[fType].forecast.byTier, (sum: number, obj: any) => {
      return sum + obj.imps;
    }, 0);
    this.forecastStates[fType].forecast.totalRenderedAllocated = _.reduce(this.forecastStates[fType].forecast.byTier, (sum: number, obj: any) => {
      return sum + obj.rends;
    }, 0);

    this.forecastStates[fType].forecast.topCompsArray = _.take(
      _.orderBy(this.forecastStates[fType].forecast.compArray, ['sameTargeting', 'matchingSimilarity'], ['desc', 'desc']),
      20
    );

    this.forecastStates[fType].forecast.topCompsArray.forEach((li: any) => {
      for (const key in li.bidSpecification) {
        const val = li.bidSpecification[key];
        if (Array.isArray(val) && val.length === 0) {
          delete li.bidSpecification[key];
        }
      }
    });

    this.forecastStates[fType][pendingParam] = false;
  }

  private publishForecastState(fType: string): void {
    this.forecastStates = {
      ...this.forecastStates,
      [fType]: {...this.forecastStates[fType]}
    };
  }

  private sortComparator(v1: {value: any}, v2: {value: any}): number {
    let v1Value = v1.value || 0;
    let v2Value = v2.value || 0;
    if (this.sorting.orderByParam === 'bidSpecification') {
      _.forEach(v1.value, (val: any) => {
        if (val.amount >= 0) {
          v1Value = val.amount;
          return false;
        }
      });
      _.forEach(v2.value, (val: any) => {
        if (val.amount >= 0) {
          v2Value = val.amount;
          return false;
        }
      });
    }
    return (v1Value < v2Value) ? -1 : 1;
  }

  private getSortValue(item: any, param: string): any {
    if (param === 'bidSpecification') {
      return item.bidSpecification;
    }
    return item[param];
  }
}
